/**
 * Générateur — Soustraire deux nombres décimaux relatifs.
 * Attendu ID : 918 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 918.json) : « Soustraire deux nombres
 * décimaux relatifs. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « construction » — Comment soustraire ? (la règle)
 *     qcm_unique : par quelle ADDITION remplacer la soustraction — les quatre
 *     combinaisons de signes en propositions, la bonne dépendant du tirage ;
 *     texte_trou_drag : la règle appliquée, l'opposé puis le résultat ;
 *     tableau_vf : six gabarits à prédicat variable, qui portent les erreurs
 *     que le calcul seul ne révèle pas.
 *
 *   Carte « mental » — Calcul mental (niveaux 1 et 2)
 *     Entiers relatifs, les quatre couples de signes, les trois écritures.
 *     texte_trou_saisie et qcm_unique (distracteurs nommés).
 *
 *   Carte « transformer » — Transformer la soustraction en addition (niveau 3)
 *     texte_trou_saisie en TROIS CHAMPS étiquetés — le premier terme recopié,
 *     l'opposé du second, le résultat — avec barème partiel annoncé. Le second
 *     terme est toujours négatif : soustraire un positif n'exige pas l'étape.
 *
 *   Carte « decimaux » — Soustractions de décimaux (niveaux 4 et 5)
 *     texte_trou_saisie : un terme à une décimale et l'autre entier, ou les
 *     deux à deux décimales, avec des valeurs absolues inférieures à 1.
 *
 *   Carte « trou » — La soustraction à trou (niveau 6)
 *     texte_trou_saisie et qcm_unique, les DEUX positions du trou.
 *
 * Spécification (918.json) :
 *   Bornes appliquées : nombre_de_termes = 2 exactement (`origine: prescrit`) ;
 *     nombre_de_decimales_de_chaque_terme 0..2 (`origine: prescrit`) appliqué à
 *     CHAQUE terme ; premier_terme_relatif et second_terme_relatif -20..20,
 *     aucun terme nul, valeurs absolues inférieures à 1 rencontrées ;
 *     repartition_des_signes = les quatre couples, (+,+) une fois sur dix
 *     (au plus une question sur six) ;
 *     forme_d_ecriture_de_l_enonce = les trois écritures de la liste fermée,
 *     l'écriture simplifiée n'étant employée que si le second terme est positif
 *     (voir « Non tranché ») ;
 *     regle_de_transformation = étape écrite et corrigée à part au niveau 3 ;
 *     tache_demandee = calculer, transformer puis calculer, terme manquant.
 *   Exclusions : aucun terme nul ; pas plus d'une question sur six à deux
 *     termes positifs ; aucune imbrication de parenthèses ni troisième terme ;
 *     l'écriture attendue dans un trou est annoncée par la consigne ; aucune
 *     différence nulle construite ; aucun libellé annonçant « entiers
 *     relatifs » (aucun libellé de carte ne nomme le type des nombres).
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, erreurVisee) :
 *     « Appliquer la règle des signes de la multiplication à une soustraction »
 *         → regle_des_signes_de_la_multiplication (construction ×2, mental_qcm)
 *     « Ôter la parenthèse d'un terme négatif sans changer le signe »
 *         → parenthese_otee_sans_changer_le_signe (construction_vf, mental_qcm,
 *           decimaux)
 *     « Croire que soustraire revient à ajouter tout court »
 *         → ajouter_sans_opposer (construction ×2, mental_qcm, trou_qcm)
 *     « Prendre l'opposé du premier terme au lieu du second »
 *         → oppose_du_premier_terme (construction ×2, mental_qcm)
 *     « Écrire deux signes côte à côte faute de parenthèse »
 *         → deux_signes_cote_a_cote (construction_vf)
 *     « Dans une soustraction à trou, ajouter au lieu de retrancher, ou
 *       retrancher du mauvais côté » → trou_mauvais_sens (trou ×2)
 *     « Aligner mal les rangs décimaux quand les deux termes n'ont pas le même
 *       nombre de décimales » → rangs_decimaux_mal_alignes (construction_vf,
 *       decimaux quand les décimales diffèrent)
 *   Graduation → cartes :
 *     niveau 1 (entiers de signes contraires) et niveau 2 (les quatre couples,
 *       entiers) → carte mental ; niveau 3 (transformation en trois champs) →
 *       carte transformer ; niveau 4 (un terme à une décimale, l'autre entier)
 *       et niveau 5 (deux décimales, valeurs absolues inférieures à 1) → carte
 *       decimaux ; niveau 6 (soustraction à trou, les deux positions) → carte
 *       trou. La carte construction porte la règle elle-même.
 *   Non tranché : amplitude_maximale_reglable n'a pas de mécanisme de réglage ;
 *     l'écriture simplifiée d'une soustraction d'un négatif EST la
 *     transformation attendue, elle n'est donc employée que si le second terme
 *     est positif ; « série » de questions n'a pas d'équivalent.
 *
 * Arithmétique en CENTIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 918;

const L_CONSTRUCTION = 'Comment soustraire ? (la règle)';
const L_MENTAL = 'Calcul mental';
const L_TRANSFORMER = 'Transformer la soustraction en addition';
const L_DECIMAUX = 'Soustractions de décimaux';
const L_TROU = 'La soustraction à trou';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centièmes entiers → écriture française. cent(0) = « 0 ». */
const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};
const signe = (c) => (c === 0 ? '0' : c < 0 ? cent(c) : `+${cent(c)}`);
const paren = (c) => `(${signe(c)})`;

const CONSIGNE_RES = 'Écris le résultat en écriture décimale ; un résultat positif peut s\'écrire avec ou sans le signe +.';
const CONSIGNE_TROU = 'Écris le nombre manquant sans parenthèses ; un nombre positif peut s\'écrire avec ou sans le signe +.';

// ═════════ Tirage des termes ═════════

/** Un terme non nul, |v| ≤ 20, avec exactement `dec` décimales. */
const tirerTerme = (dec) => {
    if (dec === 0) return randInt(1, 20) * 100;
    if (dec === 1) {
        let k = randInt(1, 199);
        if (k % 10 === 0) k += 1;
        return k * 10;
    }
    let k = randInt(1, 1999);
    if (k % 10 === 0) k += 1;
    return k;
};

/** repartition_des_signes : les quatre couples, (+,+) une fois sur dix. */
const SIGNES = ['pn', 'pn', 'pn', 'np', 'np', 'np', 'nn', 'nn', 'nn', 'pp'];
const SIGNES_CONTRAIRES = ['pn', 'np'];

const appliquerSignes = (cas, x, y) => {
    if (cas === 'pp') return [x, y];
    if (cas === 'nn') return [-x, -y];
    return cas === 'pn' ? [x, -y] : [-x, y];
};

/** Couples de décimales par niveau (niveau 4 : un terme entier, l'autre non). */
const DECIMALES = { 1: [0, 0], 2: [0, 0], 4: [1, 0], 5: [2, 2] };

const tirerCouple = (niveau, cas) => {
    const [d1, d2] = DECIMALES[niveau];
    const [da, db] = randomChoice([[d1, d2], [d2, d1]]);
    return appliquerSignes(cas, tirerTerme(da), tirerTerme(db));
};

// ═════════ Écriture de l'énoncé (liste fermée de la spec) ═════════

/**
 * L'écriture simplifiée d'une soustraction d'un NÉGATIF est exactement la
 * transformation attendue (a - (-b) devient a + b) : elle n'est donc tirée que
 * si le second terme est positif.
 */
const tirerForme = (b) => (b > 0 ? randomChoice(['tous', 'negatifs', 'simplifiee']) : randomChoice(['tous', 'negatifs']));

const ecrireSoustraction = (a, b, forme) => {
    if (forme === 'tous') return `${paren(a)} - ${paren(b)}`;
    if (forme === 'negatifs') {
        const g = a < 0 ? `(${cent(a)})` : cent(a);
        const d = b < 0 ? `(${cent(b)})` : cent(b);
        return `${g} - ${d}`;
    }
    return `${cent(a)} - ${cent(b)}`; // b > 0 par construction
};

/** Décalage des rangs décimaux : 2,5 devient 2,05 (erreur visée 7). */
const malAligne = (c) => {
    const s = c < 0 ? -1 : 1;
    const a = Math.abs(c);
    return s * (Math.floor(a / 100) * 100 + Math.floor((a % 100) / 10));
};

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

/**
 * Un couple dont les quatre combinaisons de signes donnent quatre valeurs
 * distinctes : a ≠ b, a + b ≠ 0, et les deux termes non nuls.
 */
const tirerCoupleDistinct = (niveau, cas) => {
    let [a, b] = tirerCouple(niveau, cas);
    for (let essai = 0; essai < 60 && (a === b || a + b === 0); essai++) [a, b] = tirerCouple(niveau, cas);
    if (a === b || a + b === 0) { a = 300; b = -1100; } // repli déterministe
    return [a, b];
};

// ═════════ Carte « construction » — la règle ═════════

const genConstructionQcm = () => {
    const niveau = randomChoice([1, 2, 4, 5]);
    const [a, b] = tirerCoupleDistinct(niveau, randomChoice(SIGNES));
    const forme = tirerForme(b);
    // Les quatre additions possibles : la bonne dépend du signe de chaque terme,
    // aucune proposition n'est reconnaissable à sa seule forme.
    const propositions = [
        { valeur: `${cent(a)} + ${paren(-b)}`, code: null },
        { valeur: `${cent(a)} + ${paren(b)}`, code: 'ajouter_sans_opposer' },
        { valeur: `${cent(-a)} + ${paren(-b)}`, code: 'oppose_du_premier_terme' },
        { valeur: `${cent(-a)} + ${paren(b)}`, code: 'regle_des_signes_de_la_multiplication' },
    ];
    const payload = makeQcm(propositions.map(p => p.valeur), propositions[0].valeur,
        `<p><b>Par quelle addition peut-on remplacer ${ecrireSoustraction(a, b, forme)} ?</b></p>`,
        opts(L_CONSTRUCTION));
    payload._meta.params = {
        a, b, forme, niveau,
        distracteurs: propositions.slice(1),
    };
    return payload;
};

const genConstructionDrag = () => {
    const niveau = randomChoice([1, 2, 4, 5]);
    const [a, b] = tirerCoupleDistinct(niveau, randomChoice(SIGNES));
    const forme = tirerForme(b);
    const phrase = `${ecrireSoustraction(a, b, forme)} = ${cent(a)} + {{${signe(-b)}}} = {{${signe(a - b)}}}`;
    const candidats = [signe(b), signe(a + b), signe(-(a - b)), signe(-a)];
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète : soustraire un nombre relatif revient à additionner son opposé.</b></p>',
        { ...opts(L_CONSTRUCTION), mode: 'drag', distracteurs });
    payload._meta.params = {
        a, b, forme, niveau,
        distracteurs: distracteurs.map(d => ({ valeur: d, code: 'ajouter_sans_opposer' })),
    };
    return payload;
};

/**
 * Gabarits à prédicat variable. ctx : { a, b (a - b), p (positif), n (négatif),
 * x (une décimale), y (deux décimales) }.
 */
const GABARITS_CONSTRUCTION = [
    {
        id: 'regle', code: 'ajouter_sans_opposer',
        ligne: (v, c) => `Soustraire ${paren(c.n)} revient à ajouter ${cent(v ? -c.n : c.n)}.`,
    },
    {
        id: 'parenthese', code: 'deux_signes_cote_a_cote',
        ligne: (v, c) => `L'écriture ${cent(c.p)} - ${v ? paren(c.n) : cent(c.n)} est correcte.`,
    },
    {
        id: 'oppose_premier', code: 'oppose_du_premier_terme',
        ligne: (v, c) => `Pour transformer ${paren(c.a)} - ${paren(c.b)} en addition, on change le signe du `
            + `${v ? 'second' : 'premier'} terme.`,
    },
    {
        id: 'otee', code: 'parenthese_otee_sans_changer_le_signe',
        ligne: (v, c) => `Dans ${cent(c.p)} - ${paren(c.n)}, on peut enlever les parenthèses et écrire `
            + `${cent(c.p)} ${v ? '+' : '-'} ${cent(-c.n)}.`,
    },
    {
        id: 'signes', code: 'regle_des_signes_de_la_multiplication',
        ligne: (v, c) => `${paren(c.a)} - ${paren(c.b)} = ${cent(v ? c.a - c.b : -(c.a - c.b))}.`,
    },
    {
        id: 'alignement', code: 'rangs_decimaux_mal_alignes',
        ligne: (v, c) => `${cent(c.x)} - ${cent(c.y)} = ${cent(v ? c.x - c.y : malAligne(c.x) - c.y)}.`,
    },
];

const genConstructionVf = () => {
    const [a, b] = tirerCoupleDistinct(randomChoice([1, 2, 4, 5]), randomChoice(SIGNES));
    const ctx = {
        a, b,
        p: tirerTerme(randomChoice([0, 1])),
        n: -tirerTerme(randomChoice([0, 1])),
        x: tirerTerme(1),
        y: tirerTerme(2),
    };
    const gabarits = shuffle(GABARITS_CONSTRUCTION).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_CONSTRUCTION));
    payload._meta.params = { ...ctx, niveau: 2, lignes };
    return payload;
};

// ═════════ Cartes « mental » et « decimaux » — calculer ═════════

const genCalculSaisie = (niveaux, label) => () => {
    const niveau = randomChoice(niveaux);
    const cas = randomChoice(niveau === 1 ? SIGNES_CONTRAIRES : SIGNES);
    const [a, b] = tirerCouple(niveau, cas);
    const forme = tirerForme(b);
    const phrase = `${ecrireSoustraction(a, b, forme)} = {{${cent(a - b)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(label), mode: 'saisie' });
    payload._meta.params = {
        a, b, forme, cas, niveau,
        erreurVisee: (a % 100 === 0) !== (b % 100 === 0)
            ? 'rangs_decimaux_mal_alignes'
            : 'parenthese_otee_sans_changer_le_signe',
    };
    return payload;
};

const genMentalQcm = () => {
    const niveau = randomChoice([1, 2]);
    const cas = randomChoice(niveau === 1 ? SIGNES_CONTRAIRES : SIGNES);
    const [a, b] = tirerCoupleDistinct(niveau, cas);
    const forme = tirerForme(b);
    const distracteurs = [
        { valeur: cent(a + b), code: 'ajouter_sans_opposer' },
        { valeur: cent(-a - b), code: 'oppose_du_premier_terme' },
        { valeur: cent(b - a), code: 'regle_des_signes_de_la_multiplication' },
    ];
    const payload = makeQcm([cent(a - b), ...distracteurs.map(d => d.valeur)], cent(a - b),
        `<p><b>Calcule mentalement : ${ecrireSoustraction(a, b, forme)}</b></p>`,
        opts(L_MENTAL));
    payload._meta.params = { a, b, forme, cas, niveau, distracteurs };
    return payload;
};

// ═════════ Carte « transformer » — trois champs (niveau 3) ═════════

const genTransformer = () => {
    // Le second terme est TOUJOURS négatif : soustraire un positif n'exige pas
    // l'étape de transformation (points_de_vigilance).
    const niveau = randomChoice([2, 4, 5]);
    const [a, b] = tirerCouple(niveau, randomChoice(['pn', 'nn']));
    const forme = randomChoice(['tous', 'negatifs']);
    // Trois champs ÉTIQUETÉS, jamais deux trous autour du même « + » : l'ordre
    // des lignes est imposé par leur libellé.
    const phrase = `Calcul : ${ecrireSoustraction(a, b, forme)}\n`
        + `Premier terme : {{${cent(a)}}}\n`
        + `Opposé du second terme : {{${cent(-b)}}}\n`
        + `Résultat : {{${cent(a - b)}}}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Transforme la soustraction en addition, puis calcule.</b> '
        + `${CONSIGNE_RES} Chaque champ compte pour un point : la transformation et le résultat sont notés séparément.</p>`,
        { ...opts(L_TRANSFORMER), mode: 'saisie' });
    payload._meta.params = { a, b, forme, niveau: 3, erreurVisee: 'oppose_du_premier_terme' };
    return payload;
};

// ═════════ Carte « trou » — La soustraction à trou (niveau 6) ═════════

const genTrouSaisie = () => {
    const niveau = randomChoice([2, 4, 5]);
    const [a, b] = tirerCoupleDistinct(niveau, randomChoice(SIGNES));
    const forme = randomChoice(['tous', 'negatifs']);
    const position = randInt(0, 1);
    const trou = (v) => `{{${cent(v)}}}`;
    const membre = forme === 'tous'
        ? `${position === 0 ? `(${trou(a)})` : paren(a)} - ${position === 1 ? `(${trou(b)})` : paren(b)}`
        : `${position === 0 ? trou(a) : (a < 0 ? `(${cent(a)})` : cent(a))} - `
          + `${position === 1 ? trou(b) : (b < 0 ? `(${cent(b)})` : cent(b))}`;
    const payload = makeTexteTrou(`${membre} = ${cent(a - b)}`,
        `<p><b>Complète l'égalité.</b> ${CONSIGNE_TROU}</p>`,
        { ...opts(L_TROU), mode: 'saisie' });
    payload._meta.params = { a, b, forme, position, niveau: 6, erreurVisee: 'trou_mauvais_sens' };
    return payload;
};

/**
 * Les quatre propositions d'une soustraction à trou : le terme cherché, son
 * opposé, et les deux erreurs de sens (ajouter au lieu de retrancher, et
 * retrancher du mauvais côté de l'égalité).
 */
const propositionsTrou = (a, b, position) => {
    const d = a - b;
    const cherche = position === 0 ? a : b;
    return position === 0
        ? [cherche, -cherche, d - b, b - d]
        : [cherche, -cherche, a + d, d];
};

const genTrouQcm = () => {
    const niveau = randomChoice([2, 4, 5]);
    const position = randInt(0, 1);
    const invalide = (x, y) => new Set(propositionsTrou(x, y, position)).size < 4
        || x - y === 0 || (position === 0 ? x : y) === 0;
    let [a, b] = tirerCoupleDistinct(niveau, randomChoice(SIGNES));
    for (let essai = 0; essai < 60 && invalide(a, b); essai++) {
        [a, b] = tirerCoupleDistinct(niveau, randomChoice(SIGNES));
    }
    if (invalide(a, b)) { a = 700; b = -300; } // repli déterministe
    const [cherche, ...autres] = propositionsTrou(a, b, position);
    const codes = ['ajouter_sans_opposer', 'trou_mauvais_sens', 'trou_mauvais_sens'];
    const distracteurs = autres.map((v, i) => ({ valeur: cent(v), code: codes[i] }));
    const forme = randomChoice(['tous', 'negatifs']);
    const gauche = position === 0 ? '…' : (forme === 'tous' ? paren(a) : (a < 0 ? `(${cent(a)})` : cent(a)));
    const droite = position === 1 ? '…' : (forme === 'tous' ? paren(b) : (b < 0 ? `(${cent(b)})` : cent(b)));
    const payload = makeQcm([cent(cherche), ...distracteurs.map(d => d.valeur)], cent(cherche),
        `<p><b>Quel nombre complète l'égalité ${gauche} - ${droite} = ${cent(a - b)} ?</b></p>`,
        opts(L_TROU));
    payload._meta.params = { a, b, forme, position, niveau: 6, distracteurs };
    return payload;
};
// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    construction_qcm_unique: genConstructionQcm,
    construction_texte_trou_drag: genConstructionDrag,
    construction_vf: genConstructionVf,
    mental_texte_trou_saisie: genCalculSaisie([1, 2], L_MENTAL),
    mental_qcm_unique: genMentalQcm,
    transformer_texte_trou_saisie: genTransformer,
    decimaux_texte_trou_saisie: genCalculSaisie([4, 5, 5], L_DECIMAUX),
    trou_texte_trou_saisie: genTrouSaisie,
    trou_qcm_unique: genTrouQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous', vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'construction', label: L_CONSTRUCTION, formatsList: buildFormats('construction') },
    { id: 'mental', label: L_MENTAL, formatsList: buildFormats('mental') },
    { id: 'transformer', label: L_TRANSFORMER, formatsList: buildFormats('transformer') },
    { id: 'decimaux', label: L_DECIMAUX, formatsList: buildFormats('decimaux') },
    { id: 'trou', label: L_TROU, formatsList: buildFormats('trou') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
